import { useEffect, useState } from "react";
import axios from "axios";
import { Download, Filter, Timer } from "lucide-react";
import { API, fmtErr, fmtSec, money } from "../lib/pos";
import { useAuth } from "../context/AuthContext";
import { DateRangeField, clampRange } from "../components/DateField";

export function Reports({ stations }) {
  const { user } = useAuth();
  const isManager = user.role === "manager";
  const initialId = isManager ? (stations[0]?.id || "") : user.station_id;
  const [stationId, setStationId] = useState(initialId);
  const today = new Date().toISOString().slice(0, 10);
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const applyPreset = (which) => {
    const t = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (which === "today") { setStart(iso(t)); setEnd(iso(t)); }
    else if (which === "yesterday") { const y = new Date(t); y.setDate(t.getDate() - 1); setStart(iso(y)); setEnd(iso(y)); }
    else if (which === "7d") { const s = new Date(t); s.setDate(t.getDate() - 6); setStart(iso(s)); setEnd(iso(t)); }
    else if (which === "30d") { const s = new Date(t); s.setDate(t.getDate() - 29); setStart(iso(s)); setEnd(iso(t)); }
    else if (which === "mtd") { const s = new Date(t.getFullYear(), t.getMonth(), 1); setStart(iso(s)); setEnd(iso(t)); }
  };

  const load = async () => {
    if (!stationId) return;
    setBusy(true);
    try {
      const r = await axios.get(`${API}/stations/${stationId}/report`, { params: { start, end } });
      setReport(r.data);
    } catch (e) { alert(fmtErr(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [stationId, start, end]);

  const exportCsv = () => {
    const url = `${API}/stations/${stationId}/report/export.csv?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`;
    window.open(url, "_blank");
  };

  return (
    <div className="workspace">
      <div className="page-head">
        <div>
          <p className="eyebrow">STATION REPORTS</p>
          <h1>{isManager ? "Reports" : "My station reports"}</h1>
          <p className="muted">Sales, item counts, ticket times and completed orders — filter and export.</p>
        </div>
        <button data-testid="export-csv-button" className="outline" onClick={exportCsv} disabled={!report}><Download size={14} style={{ marginRight: 6 }} />Export CSV</button>
      </div>

      <div className="report-filters">
        {isManager && (
          <label className="field">Station
            <select data-testid="report-station-select" value={stationId} onChange={(e) => setStationId(e.target.value)}>
              {stations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
        )}
        <label className="field">Range<DateRangeField start={start} end={end} onChange={({ start: s, end: e }) => { setStart(s); setEnd(e); }} testPrefix="report" /></label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button data-testid="report-preset-today" className="outline" onClick={() => applyPreset("today")}>Today</button>
          <button data-testid="report-preset-yesterday" className="outline" onClick={() => applyPreset("yesterday")}>Yesterday</button>
          <button data-testid="report-preset-7d" className="outline" onClick={() => applyPreset("7d")}>Last 7d</button>
          <button data-testid="report-preset-30d" className="outline" onClick={() => applyPreset("30d")}>Last 30d</button>
          <button data-testid="report-preset-mtd" className="outline" onClick={() => applyPreset("mtd")}>Month-to-date</button>
        </div>
        <button data-testid="report-run-button" className="primary" onClick={load} disabled={busy}><Filter size={14} style={{ marginRight: 6 }} />{busy ? "Loading..." : "Apply"}</button>
      </div>

      {report && (
        <>
          <div className="metrics" style={{ marginTop: 22 }}>
            <div><span>Sales total</span><b>{money(report.sales_total)}</b><small>Item + GST − Discount</small></div>
            <div><span>Item value</span><b>{money(report.item_total || 0)}</b><small>before GST + discount</small></div>
            <div><span>GST</span><b>{money(report.gst_total || 0)}</b><small>station's slice of order GST</small></div>
          </div>
          <div className="metrics" style={{ marginTop: 12 }}>
            <div><span>Items sold</span><b>{report.item_count}</b><small>{report.completed_orders_count} tickets</small></div>
            <div><span>Discount</span><b>{money(report.discount_total || 0)}</b><small>prorated to this station</small></div>
            <div><span>Avg ticket time</span><b>{report.avg_ticket_seconds ? fmtSec(report.avg_ticket_seconds) : "—"}</b><small><Timer size={11} style={{ verticalAlign: "middle" }} /> from send to ready</small></div>
          </div>

          <div className="panel" style={{ marginTop: 20 }}>
            <h2 style={{ marginBottom: 20 }}>Item breakdown</h2>
            {report.item_breakdown.length ? (
              <div className="report-table">
                <div className="rt-head"><span>Item</span><span>Category</span><span>Qty</span><span>Revenue</span></div>
                {report.item_breakdown.map((it) => (
                  <div className="rt-row" key={it.name}>
                    <b>{it.name}</b>
                    <em>{it.category}</em>
                    <span>{it.quantity}</span>
                    <strong>{money(it.revenue)}</strong>
                  </div>
                ))}
              </div>
            ) : <p className="muted">No items sold in this range.</p>}
          </div>

          <div className="panel" style={{ marginTop: 20 }}>
            <h2 style={{ marginBottom: 20 }}>Completed orders</h2>
            {report.orders.length ? (
              <div className="report-table">
                <div className="rt-head order-head"><span>Order #</span><span>Type</span><span>Created</span><span>Ready</span><span>Status</span><span>Revenue</span></div>
                {report.orders.map((o) => (
                  <div className="rt-row order-row" key={o.order_id}>
                    <b>{o.order_number != null ? `#${String(o.order_number).padStart(4, "0")}` : o.order_id.slice(-8)}{o.table ? ` · T${o.table}` : ""}</b>
                    <em>{o.order_type}</em>
                    <span>{new Date(o.created_at).toLocaleString()}</span>
                    <span>{o.ready_at ? new Date(o.ready_at).toLocaleString() : "—"}</span>
                    <span className={`badge ${o.status}`}>{o.status}</span>
                    <strong>{money(o.station_revenue)}</strong>
                  </div>
                ))}
              </div>
            ) : <p className="muted">No completed orders in this range.</p>}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------- User Management (manager-only) ---------- */
